import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '#lib/cn.ts';

/**
 * The body type scale Mantine's `Text` override applied in `apps/web`. Weight and letter spacing
 * are set once on `body` (`styles/tailwind.css`), so each step carries only its font-size/line-height
 * pair — except `xl`, the one step that drops to 400. `md` is the default because that is what
 * Mantine's `<Text>` inherited from the body.
 *
 * This is hand-authored rather than vendored: nothing in shadcn covers a body scale, and the ~108
 * `<Text>` call sites in `apps/internal` are worth owning one component for.
 */
const textVariants = cva('', {
  variants: {
    size: {
      xs: 'text-xs/[1.25]',
      sm: 'text-sm/[1.4]',
      md: 'text-base/[1.5]',
      lg: 'text-lg/[1.6]',
      xl: 'text-2xl/[1.5] font-normal',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

function Text({
  className,
  size = 'md',
  render,
  ...props
}: useRender.ComponentProps<'p'> & VariantProps<typeof textVariants>) {
  return useRender({
    defaultTagName: 'p',
    props: mergeProps<'p'>(
      {
        className: cn(textVariants({ size }), className),
      },
      props,
    ),
    render,
    state: {
      slot: 'text',
      size,
    },
  });
}

export { Text, textVariants };
